import { EventEmitter, Injectable } from '@angular/core';

import { UtilsService } from '@services/utils.service';
import { OrigemLancamento } from '../../../../shared/models/enums/origem-lancamento';
import { isTarifaItau } from '../models/constants/tarifas-banco';
import { LancamentoPrincipal } from '../models/lancamento-principal';
import { BancoOrigemEnum } from '../../../../shared/models/enums/banco-origem-enum';
import { DadosConciliacao } from '../models/dados-conciliacao';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { MatDialog } from '@angular/material/dialog';
import { CorrespondenciaConciliacao } from '../models/correspondencia-conciliacao';
import { MessageService } from '@services/message.service';
import { ModoPaginaEnum } from '../../../../models/enum/modo-pagina-enum';
import { InformacoesSalvarConciliacao } from '../models/informacoes-salvar-conciliacao';
import { CorrespondenciasIdsConciliacao } from '../models/correspondencias-ids-conciliacao';
import { CurrencyPipe } from '@angular/common';
import { LancamentoAgrupado } from '../models/lancamento-agrupado';
import { isLancamentoAgrupado, isLancamentoPrincipal, Lancamento } from '../models/lancamento';
import { ModoBuscaDataLancamentoEnum } from '../models/enum/modo-busca-data-lancamento-enum';
import { ResumoBuscaEmMassa } from '../models/resumo-busca-em-massa';
import { ConciliacaoApiService } from './conciliacao-api.service';

// Resultado da busca de um item durante a busca em massa, coletado antes de decidir o que
// conciliar (ver executarBuscaEmMassa / resolverEConciliar).
interface ResultadoBuscaEmMassa {
  item: LancamentoPrincipal;
  correspondencias: Lancamento[][];
  timeout: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConciliacaoService {

  dadosConciliacao: DadosConciliacao;
  onUpdateDadosConciliacao: EventEmitter<boolean> = new EventEmitter<boolean>();
  tarifasAdicionadas: number = 0;
  get modoPagina(): ModoPaginaEnum { return this.dadosConciliacao.modoPagina; }
  get modoEdicao(): boolean { return this.modoPagina === ModoPaginaEnum.Edicao; }
  get tipoBanco(): BancoOrigemEnum { return this.dadosConciliacao.tipoBanco; }

  // Busca
  searchValue: number = 0;
  searching: boolean = false;
  targetIsPositive: boolean = true;
  diferencaValoresSelecionados: number = 0;
  buscarEm: OrigemLancamento = OrigemLancamento.Banco;
  correspondenciasConciliacao: CorrespondenciaConciliacao[] = [];
  searchWorker: Worker | null = null;
  searchTimeout: any = null;
  // Guarda o resolve da busca em andamento para permitir interromper (busca em massa).
  private resolveBuscaAtual: ((resultado: { resultados: Lancamento[][]; timeout: boolean }) => void) | null = null;
  applySearch: EventEmitter<boolean> = new EventEmitter<boolean>();
  exibeCorrespondencia: EventEmitter<number> = new EventEmitter<number>();
  updateCorrespondenciaConciliacaoIndex: EventEmitter<number | null> = new EventEmitter<number | null>();

  // Banco
  get lancamentosBanco(): LancamentoPrincipal[] { return this.dadosConciliacao.lancamentosBanco; }
  lancamentosPositivosBanco: LancamentoPrincipal[] = [];
  lancamentosNegativosBanco: LancamentoPrincipal[] = [];
  lancamentosSelecionadosBanco: LancamentoPrincipal[] = [];
  somaLancamentosSelecionadosBanco: number = 0;
  lancamentosBancoMap: Map<number, LancamentoPrincipal> = new Map<number, LancamentoPrincipal>();
  onUpdateSelectionBanco: EventEmitter<void> = new EventEmitter<void>();


  // Caixa
  get lancamentosCaixa(): LancamentoPrincipal[] { return this.dadosConciliacao.lancamentosCaixa; }
  lancamentosPositivosCaixa: LancamentoPrincipal[] = [];
  lancamentosNegativosCaixa: LancamentoPrincipal[] = [];
  lancamentosSelecionadosCaixa: LancamentoPrincipal[] = [];
  somaLancamentosSelecionadosCaixa: number = 0;
  lancamentosCaixaMap: Map<number, LancamentoPrincipal> = new Map<number, LancamentoPrincipal>();
  onUpdateSelectionCaixa: EventEmitter<void> = new EventEmitter<void>();

  // Filtros
  buscarSubtabelas: boolean = false;
  modoBusca: ModoBuscaDataLancamentoEnum = ModoBuscaDataLancamentoEnum.DIA_UNICO;
  dataInicioBusca: Date;
  dataFimBusca: Date;
  profundidadeBusca: number = 3;
  tempoLimiteBusca: number = 15;
  // Lado cujos lançamentos são usados como base (origem) na busca em massa; a busca por
  // correspondências roda sempre no lado oposto (destino).
  origemBuscaEmMassa: OrigemLancamento = OrigemLancamento.Caixa;

  constructor(
    private utilsService: UtilsService,
    private dialog: MatDialog,
    private messageService: MessageService,
    private currencyPipe: CurrencyPipe,
    private conciliacaoApiService: ConciliacaoApiService,
  ) {
    this.dadosConciliacao = {
      contaId: 0,
      contaCaixaId: 0,
      conciliacaoId: 0,
      nomeBanco: "",
      data: new Date(),
      saldo: 0,
      valorNoCaixa: 0,
      diferencaAnterior: 0,
      tipoBanco: BancoOrigemEnum.Itau,
      lancamentosBanco: [],
      lancamentosCaixa: [],
      correspondencias: [],
      correspondenciasIdsAntigos: [],
      modoPagina: ModoPaginaEnum.Visualizacao
    };
  }

  public updateValoresSelecionados(lancamentosSelecionados: LancamentoPrincipal[], cardEhBanco: boolean): number {
    let somaLancamentosSelecionados = 0;
    lancamentosSelecionados.forEach(v => somaLancamentosSelecionados += v.valorX100);
    somaLancamentosSelecionados /= 100;

    if (cardEhBanco) {
      this.lancamentosSelecionadosBanco = lancamentosSelecionados;
      this.somaLancamentosSelecionadosBanco = somaLancamentosSelecionados;
      this.buscarEm = OrigemLancamento.Caixa;
    }
    else {
      this.lancamentosSelecionadosCaixa = lancamentosSelecionados;
      this.somaLancamentosSelecionadosCaixa = somaLancamentosSelecionados;
      this.buscarEm = OrigemLancamento.Banco;
    }

    if (somaLancamentosSelecionados > 0) {
      this.targetIsPositive = false;
      this.searchValue = somaLancamentosSelecionados;
    }
    else {
      this.targetIsPositive = true;
      this.searchValue = somaLancamentosSelecionados * -1;
    }

    this.diferencaValoresSelecionados = this.somaLancamentosSelecionadosBanco + this.somaLancamentosSelecionadosCaixa;

    if (lancamentosSelecionados.length === 1 && lancamentosSelecionados[0].conciliado) {
      const correspondenciaIndex = this.getCorrespondenciaIndex(lancamentosSelecionados[0], cardEhBanco);

      if (correspondenciaIndex >= 0)
        this.exibeCorrespondencia.emit(correspondenciaIndex);
      else
        this.applySearch.emit(false);
    }
    else {
      this.applySearch.emit(!lancamentosSelecionados.some(l => l.conciliado));
    }

    return somaLancamentosSelecionados;
  }

  public getCorrespondenciaIndex(lancamentoSelecionado: Lancamento, cardEhBanco: boolean): number {
    if (cardEhBanco)
      return this.correspondenciasConciliacao.findIndex(m => m.lancamentosBanco.find(l => l.id === lancamentoSelecionado.id) !== undefined);
    else
      return this.correspondenciasConciliacao.findIndex(m => m.lancamentosCaixa.find(l => l.id === lancamentoSelecionado.id) !== undefined);
  }

  // Busca manual (chamada pela tela): mantém o contrato antigo — resolve direto com a lista de
  // correspondências e avisa por toast quando o tempo limite é excedido.
  public searchForValue(value: number, buscarEm: OrigemLancamento, buscandoLancamentos: boolean): Promise<Lancamento[][]> {
    return this.executarBuscaNoWorker(value, buscarEm, buscandoLancamentos).then(({ resultados, timeout }) => {
      if (timeout)
        this.messageService.showToastWarning(`Busca de correspondencias cancelada por exceder tempo limite (${this.tempoLimiteBusca}s).`);
      return resultados;
    });
  }

  // Núcleo da busca por correspondências, usado tanto pela busca manual (searchForValue) quanto
  // pela busca em massa. Diferente de searchForValue, expõe se a busca estourou o tempo
  // limite ao invés de tratar isso como "nenhuma correspondência encontrada" — a busca em
  // massa precisa dessa distinção para não reportar um timeout como se fosse um lançamento
  // genuinamente sem correspondência.
  private executarBuscaNoWorker(value: number, buscarEm: OrigemLancamento, buscandoLancamentos: boolean): Promise<{ resultados: Lancamento[][]; timeout: boolean }> {
    if (this.searchWorker !== null)
      this.terminateSearchWorker("Busca anterior cancelada ao realizar outra busca");

    return new Promise((resolve, reject) => {
      if (value === undefined || value === null) return resolve({ resultados: [], timeout: false });

      if (typeof Worker === 'undefined') {
        const messageError = 'Web Workers not supported';
        this.messageService.showToastError(messageError);
        return reject(new Error(messageError));
      }

      this.searching = true;
      this.resolveBuscaAtual = resolve;

      const orderedList = this.getOrderedListForSearch(buscarEm, buscandoLancamentos);

      let search = Number((value * 100).toFixed(0));
      if (!this.targetIsPositive)
        search *= -1;

      this.searchWorker = new Worker(new URL('./search-correspondencias.worker', import.meta.url), { type: 'module' });

      if (this.tempoLimiteBusca > 0) {
        this.searchTimeout = setTimeout(() => {
          this.resolveBuscaAtual = null;
          this.terminateSearchWorker();
          return resolve({ resultados: [], timeout: true });
        }, this.tempoLimiteBusca * 1000);
      }

      this.searchWorker.onmessage = ({ data }) => {
        this.resolveBuscaAtual = null;
        this.terminateSearchWorker();
        const result: Lancamento[][] = data.results || [];
        // Obtem os objetos com a referencia correta que é perdida no worker
        const sameReferenceResult: Lancamento[][] =
          result.map(correspondencia => correspondencia.map(lancamento => buscarEm === OrigemLancamento.Banco ?
            this.lancamentosBancoMap.get(lancamento.id)! : this.lancamentosCaixaMap.get(lancamento.id)!
          ));
        const combinacoesValidas = sameReferenceResult.filter(correspondencia => !this.possuiIdsFolhaDuplicados(correspondencia));
        const resultadoSemDuplicatas = this.removerCorrespondenciasDuplicadasPorAgrupador(combinacoesValidas);
        return resolve({ resultados: resultadoSemDuplicatas, timeout: false });
      };

      this.searchWorker.onerror = (err) => {
        this.resolveBuscaAtual = null;
        const msgWarning = "Ocorreu um erro durante a busca.";
        this.terminateSearchWorker(msgWarning);
        return reject(err);
      };

      this.searchWorker.postMessage({
        arr: orderedList,
        target: search,
        targetIsPositive: this.targetIsPositive,
        profundidadeBusca: this.profundidadeBusca,
      });
    });
  }

  public terminateSearchWorker(motivo: string | null = null): void {
    if (this.searchWorker) {
      this.searchWorker.terminate();
      this.searchWorker = null;
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }

    this.searching = false;
    if (motivo !== null)
      this.messageService.showToastWarning(motivo);
  }

  // Interrompe imediatamente a busca em andamento (usado ao interromper a busca em massa).
  // Termina o worker e resolve a promise pendente — sem isso, o loop da busca em massa
  // ficaria preso esperando um resultado que nunca chegaria, já que o worker foi encerrado.
  public interromperBuscaEmAndamento(): void {
    if (!this.resolveBuscaAtual) return;

    const resolve = this.resolveBuscaAtual;
    this.resolveBuscaAtual = null;
    this.terminateSearchWorker();
    resolve({ resultados: [], timeout: false });
  }

  // Quando "buscarSubtabelas" está ativo, a lista de busca contém tanto o lançamento pai quanto
  // seus filhos individualmente. Como o valor do pai é sempre igual à soma dos filhos não
  // conciliados, a busca encontra a mesma correspondência real de duas formas (pai OU filhos),
  // gerando duplicatas equivalentes. As funções abaixo identificam e removem essas duplicatas.

  private removerCorrespondenciasDuplicadasPorAgrupador(correspondencias: Lancamento[][]): Lancamento[][] {
    const correspondenciasPorAssinatura = new Map<string, Lancamento[][]>();
    const ordemDasAssinaturas: string[] = [];

    correspondencias.forEach(correspondencia => {
      const assinatura = this.getAssinaturaCorrespondencia(correspondencia);

      if (!correspondenciasPorAssinatura.has(assinatura)) {
        correspondenciasPorAssinatura.set(assinatura, []);
        ordemDasAssinaturas.push(assinatura);
      }
      correspondenciasPorAssinatura.get(assinatura)!.push(correspondencia);
    });

    return ordemDasAssinaturas.map(assinatura =>
      this.escolherCorrespondenciaPreferida(correspondenciasPorAssinatura.get(assinatura)!)
    );
  }

  // Assinatura canônica: ids dos lançamentos "folha" envolvidos na correspondência, ordenados.
  // Um lançamento pai presente como atalho é substituído pelos ids dos seus filhos não
  // conciliados, para que a versão "pai" e a versão "filhos expandidos" gerem a mesma assinatura.
  private getAssinaturaCorrespondencia(correspondencia: Lancamento[]): string {
    return this.getIdsLeafCorrespondencia(correspondencia).sort((a, b) => a - b).join(',');
  }

  // Ids dos lançamentos "folha" (não agrupadores) envolvidos numa correspondência — um lançamento
  // pai presente como atalho é substituído pelos ids dos seus filhos não conciliados. Também usado
  // para detectar, na busca em massa, quando duas correspondências diferentes disputam o
  // mesmo lançamento do destino (ver resolverEConciliar).
  private getIdsLeafCorrespondencia(correspondencia: Lancamento[]): number[] {
    return correspondencia.flatMap(lancamento =>
      isLancamentoPrincipal(lancamento) && lancamento.ehAgrupador
        ? this.getIdsFilhosNaoConciliados(lancamento)
        : [lancamento.id]
    );
  }

  // Com "buscarSubtabelas" ativo, pai e filhos entram como candidatos separados na mesma busca (ver
  // getOrderedListForSearch), então uma combinação pode somar o pai (valor = soma dos filhos não
  // conciliados) junto com um desses mesmos filhos — batendo no valor buscado por coincidência
  // numérica, mas contando o valor desse filho duas vezes. Descartada aqui para nunca virar uma
  // correspondência válida, tanto na busca manual quanto na automática.
  private possuiIdsFolhaDuplicados(correspondencia: Lancamento[]): boolean {
    const ids = this.getIdsLeafCorrespondencia(correspondencia);
    return new Set(ids).size !== ids.length;
  }

  private getIdsFilhosNaoConciliados(lancamentoAgrupador: LancamentoPrincipal): number[] {
    return this.getFilhosNaoConciliados(lancamentoAgrupador).map(filho => filho.id);
  }

  private getFilhosNaoConciliados(lancamentoAgrupador: LancamentoPrincipal): LancamentoPrincipal[] {
    return (lancamentoAgrupador.lancamentosAgrupados ?? []).filter(filho => !filho.conciliado);
  }

  // Entre correspondências equivalentes, prioriza a que já lista os filhos individualmente
  // (nenhum item usado como atalho do pai). Se essa não existir entre as encontradas — por
  // exemplo por causa da profundidade máxima da busca — mantém a que usa o atalho do pai.
  private escolherCorrespondenciaPreferida(correspondenciasEquivalentes: Lancamento[][]): Lancamento[] {
    const correspondenciaExpandida = correspondenciasEquivalentes.find(correspondencia =>
      this.correspondenciaEstaTotalmenteExpandida(correspondencia)
    );

    return correspondenciaExpandida ?? correspondenciasEquivalentes[0];
  }

  private correspondenciaEstaTotalmenteExpandida(correspondencia: Lancamento[]): boolean {
    return !correspondencia.some(lancamento => isLancamentoPrincipal(lancamento) && lancamento.ehAgrupador);
  }

  // ─── Busca em massa ─────────────────────────────────────────────────────────
  //
  // Duas fases. 1) Busca: para cada lançamento não conciliado do lado "origemBuscaEmMassa",
  // roda a mesma busca de correspondência usada manualmente (mesmos filtros de data, profundidade,
  // tempo limite e subtabelas) no lado oposto (destino), sem conciliar nada ainda — só coleta o
  // resultado de cada item. 2) Resolução: roda sobre o que foi coletado (mesmo que a fase de busca
  // tenha sido interrompida no meio) e decide o que conciliar de fato. Rodar a resolução só no fim,
  // sobre todos os resultados de uma vez, é o que permite detectar quando dois lançamentos da
  // origem bateram com a mesma correspondência no destino (ver resolverEConciliar) — se conciliasse
  // item a item durante a busca, o segundo desses dois simplesmente não acharia mais candidato (já
  // consumido pelo primeiro) e cairia em "sem correspondência" sem indicar o conflito real.
  //
  // No banco, "buscarSubtabelas" também decide a granularidade do que é processado (ver
  // getLancamentosNaoConciliados): com a opção marcada, cada lançamento base (filho) é processado
  // individualmente, ignorando o agrupador; desmarcada, processa o grupo inteiro pelo pai.

  public async executarBuscaEmMassa(
    onProgresso: (processados: number, total: number) => void,
    deveParar: () => boolean,
    deveDescartarResultados: () => boolean,
    onBuscaIniciada: () => void = () => {}
  ): Promise<ResumoBuscaEmMassa> {
    const origemProcessamento = this.origemBuscaEmMassa;
    const origemBusca = origemProcessamento === OrigemLancamento.Banco ? OrigemLancamento.Caixa : OrigemLancamento.Banco;

    const lancamentosParaProcessar = this.getLancamentosNaoConciliados(origemProcessamento);
    const resumo: ResumoBuscaEmMassa = {
      totalProcessados: 0,
      conciliadosAutomaticamente: 0,
      semCorrespondencia: 0,
      comMultiplasCorrespondencias: 0,
      comConflitoEntreLancamentos: 0,
      comTimeout: 0,
      cancelado: false,
      origemProcessamento,
      itensConciliados: [],
      itensSemCorrespondencia: [],
      itensComMultiplasCorrespondencias: [],
      itensComConflitoEntreLancamentos: [],
      itensComTimeout: [],
    };
    onProgresso(0, lancamentosParaProcessar.length);

    // A busca por DIA_LANCAMENTO usa a seleção "oficial" da tela (lancamentosSelecionadosBanco/Caixa)
    // para saber a data do lançamento de origem. O processamento em lote pisa nesse valor a cada
    // iteração — por isso salva e restaura ao final, para não deixar a seleção da tela inconsistente.
    const selecaoBancoOriginal = this.lancamentosSelecionadosBanco;
    const selecaoCaixaOriginal = this.lancamentosSelecionadosCaixa;

    const resultadosColetados: ResultadoBuscaEmMassa[] = [];

    try {
      for (const lancamentoOrigem of lancamentosParaProcessar) {
        if (deveParar()) {
          resumo.cancelado = true;
          break;
        }

        // Pode já ter sido conciliado como parte da correspondência aceita em uma iteração anterior
        if (lancamentoOrigem.conciliado) continue;

        onBuscaIniciada();
        const { correspondencias, timeout } = await this.buscarCorrespondenciaAutomatica(lancamentoOrigem, origemBusca, origemProcessamento);

        // Reavalia após o await: a busca pode ter sido forçada a resolver por interromperBuscaEmAndamento().
        // Nesse caso o lançamento em andamento não foi de fato pesquisado e não deve entrar no resumo.
        if (deveParar()) {
          resumo.cancelado = true;
          break;
        }

        resultadosColetados.push({ item: lancamentoOrigem, correspondencias, timeout });
        onProgresso(resultadosColetados.length, lancamentosParaProcessar.length);
      }
    }
    finally {
      this.lancamentosSelecionadosBanco = selecaoBancoOriginal;
      this.lancamentosSelecionadosCaixa = selecaoCaixaOriginal;
    }

    if (!deveDescartarResultados())
      this.resolverEConciliar(resultadosColetados, origemProcessamento, resumo);

    return resumo;
  }

  // Classifica cada resultado coletado na fase de busca e concilia os que não têm conflito.
  // Dois (ou mais) lançamentos da origem podem ter buscado, cada um isoladamente, uma correspondência
  // que usa o mesmo lançamento do destino — idêntica ou só parcialmente sobreposta (ex.: um bate com
  // [A, B] e outro com [B, C]) — já que nada é conciliado durante a fase de busca, todos "acham" seu
  // candidato de forma independente. Mapear, ANTES de conciliar qualquer coisa, quais resultados usam
  // cada lançamento do destino detecta isso: se um lançamento do destino é usado por mais de um
  // resultado, NENHUM dos resultados que o usam concilia sozinho (evita escolher um "vencedor"
  // arbitrário pela ordem de processamento) — todos vão para itensComConflitoEntreLancamentos.
  private resolverEConciliar(
    resultados: ResultadoBuscaEmMassa[],
    origemProcessamento: OrigemLancamento,
    resumo: ResumoBuscaEmMassa
  ): void {
    const resultadosPorIdDestino = new Map<number, ResultadoBuscaEmMassa[]>();
    resultados.forEach(resultado => {
      if (resultado.timeout || resultado.correspondencias.length !== 1) return;

      this.getIdsLeafCorrespondencia(resultado.correspondencias[0]).forEach(id => {
        if (!resultadosPorIdDestino.has(id)) resultadosPorIdDestino.set(id, []);
        resultadosPorIdDestino.get(id)!.push(resultado);
      });
    });

    resultados.forEach(resultado => {
      resumo.totalProcessados++;

      if (resultado.timeout) {
        resumo.comTimeout++;
        resumo.itensComTimeout.push(resultado.item);
        return;
      }
      if (resultado.correspondencias.length === 0) {
        resumo.semCorrespondencia++;
        resumo.itensSemCorrespondencia.push(resultado.item);
        return;
      }
      if (resultado.correspondencias.length > 1) {
        resumo.comMultiplasCorrespondencias++;
        resumo.itensComMultiplasCorrespondencias.push(resultado.item);
        return;
      }

      const correspondencia = resultado.correspondencias[0];
      const idsDestino = this.getIdsLeafCorrespondencia(correspondencia);
      const emConflito = idsDestino.some(id => resultadosPorIdDestino.get(id)!.length > 1);

      if (emConflito) {
        resumo.comConflitoEntreLancamentos++;
        resumo.itensComConflitoEntreLancamentos.push(resultado.item);
        return;
      }

      this.conciliarAutomaticamente(resultado.item, correspondencia, origemProcessamento);
      resumo.conciliadosAutomaticamente++;
      resumo.itensConciliados.push(resultado.item);
    });
  }

  // No banco, "buscarSubtabelas" marcado troca a granularidade do processamento: em vez do
  // lançamento pai (grupo inteiro, valor = soma dos filhos), processa cada lançamento base
  // (filho) individualmente, ignorando o agrupador. Ver label dinâmica em FiltroDialogComponent.
  private getLancamentosNaoConciliados(origem: OrigemLancamento): LancamentoPrincipal[] {
    const lancamentos = origem === OrigemLancamento.Banco ? this.lancamentosBanco : this.lancamentosCaixa;

    const lancamentosBase = origem === OrigemLancamento.Banco && this.buscarSubtabelas
      ? lancamentos.flatMap(l => l.ehAgrupador ? (l.lancamentosAgrupados ?? []) : [l])
      : lancamentos;

    return lancamentosBase.filter(l => !l.conciliado && l.valorX100 !== 0);
  }

  private buscarCorrespondenciaAutomatica(
    lancamentoOrigem: LancamentoPrincipal,
    origemBusca: OrigemLancamento,
    origemProcessamento: OrigemLancamento
  ): Promise<{ correspondencias: Lancamento[][]; timeout: boolean }> {
    this.targetIsPositive = lancamentoOrigem.valor < 0;

    if (origemProcessamento === OrigemLancamento.Banco)
      this.lancamentosSelecionadosBanco = [lancamentoOrigem];
    else
      this.lancamentosSelecionadosCaixa = [lancamentoOrigem];

    return this.executarBuscaNoWorker(Math.abs(lancamentoOrigem.valor), origemBusca, true)
      .then(({ resultados, timeout }) => ({ correspondencias: resultados, timeout }));
  }

  private conciliarAutomaticamente(lancamentoOrigem: Lancamento, correspondencia: Lancamento[], origemProcessamento: OrigemLancamento): void {
    if (origemProcessamento === OrigemLancamento.Banco)
      this.fazerConciliacao([lancamentoOrigem], correspondencia);
    else
      this.fazerConciliacao(correspondencia, [lancamentoOrigem]);
  }

  public setDadosConciliacao(dadosConciliacao: DadosConciliacao): void {
    this.dadosConciliacao = dadosConciliacao;
    this.updateList(dadosConciliacao.lancamentosBanco, OrigemLancamento.Banco);
    this.updateList(dadosConciliacao.lancamentosCaixa, OrigemLancamento.Caixa);

    this.lancamentosBancoMap = this.getLancamentosMap(this.dadosConciliacao.lancamentosBanco);
    this.lancamentosCaixaMap = this.getLancamentosMap(this.dadosConciliacao.lancamentosCaixa);

    this.correspondenciasConciliacao = []
    this.dadosConciliacao.correspondencias?.forEach(correspondencia => {
      const lancamentosBanco = correspondencia.lancamentosIdBanco
        .map(id => this.lancamentosBancoMap.get(id))
        .filter((l): l is LancamentoPrincipal => !!l);

      const lancamentosCaixa = correspondencia.lancamentosIdCaixa
        .map(id => this.lancamentosCaixaMap.get(id))
        .filter((l): l is LancamentoPrincipal => !!l);

      this.fazerConciliacao(lancamentosBanco, lancamentosCaixa, correspondencia.id, true);
    });
    this.onUpdateDadosConciliacao.emit();
  }

  private getLancamentosMap(lancamentos: LancamentoPrincipal[]): Map<number, LancamentoPrincipal> {
    const map = new Map<number, LancamentoPrincipal>();

    lancamentos.forEach(lancamento => {
      map.set(lancamento.id, lancamento);
      lancamento.lancamentosAgrupados?.forEach(l => {
        map.set(l.id, l);
      });
    });

    return map;
  }

  private getOrderedListForSearch(buscarEm: OrigemLancamento, buscandoLancamentos: boolean): any[] {
    let lancamentos: LancamentoPrincipal[] = [];
    let dataInicioBusca: Date | null = null;
    let dataFimBusca: Date | null = null;

    if (buscarEm == OrigemLancamento.Caixa) {
      if (this.targetIsPositive) {
        lancamentos = this.lancamentosPositivosCaixa.filter(l => !l.conciliado && l.valorX100 !== 0);
      }
      else {
        lancamentos = this.lancamentosNegativosCaixa.filter(l => !l.conciliado && l.valorX100 !== 0);
      }
      dataInicioBusca = this.setDataBusca(this.dataInicioBusca, this.lancamentosSelecionadosBanco, buscandoLancamentos);
      dataFimBusca = this.setDataBusca(this.dataFimBusca, this.lancamentosSelecionadosBanco, buscandoLancamentos);
    }
    else if (buscarEm == OrigemLancamento.Banco) {
      if (this.targetIsPositive) {
        lancamentos = this.lancamentosPositivosBanco.filter(l => !isTarifaItau(l) && !l.conciliado && l.valorX100 !== 0);
      }
      else {
        lancamentos = this.lancamentosNegativosBanco.filter(l => !l.conciliado && l.valorX100 !== 0);
      }
      dataInicioBusca = this.setDataBusca(this.dataInicioBusca, this.lancamentosSelecionadosCaixa, buscandoLancamentos);
      dataFimBusca = this.setDataBusca(this.dataFimBusca, this.lancamentosSelecionadosCaixa, buscandoLancamentos);
    }

    if (dataInicioBusca)
      lancamentos = lancamentos.filter(l => l.data >= dataInicioBusca)
    if (dataFimBusca)
      lancamentos = lancamentos.filter(l => l.data <= dataFimBusca)

    if (!this.buscarSubtabelas) return lancamentos;

    const copiaComAgrupados = lancamentos.flatMap(lancamento =>
      [lancamento, ...(lancamento.lancamentosAgrupados ?? [])]
    ).filter(l => !l.conciliado && l.valorX100 !== 0);
    return copiaComAgrupados;
  }

  private setDataBusca(data: Date, lancamentosSelecionados: Lancamento[], buscandoLancamentos: boolean): Date | null {
    if (this.modoBusca !== ModoBuscaDataLancamentoEnum.DIA_LANCAMENTO)
      return data;
  
    console.log(!buscandoLancamentos, !lancamentosSelecionados, lancamentosSelecionados.length === 0)
    if (!buscandoLancamentos || !lancamentosSelecionados || lancamentosSelecionados.length === 0) {
      return null;
    }
  
    const datasUnicas = [
      ...new Set(
        lancamentosSelecionados
          .map(l => l.data) 
          .map(d => new Date(d).toDateString())
      )
    ];
  
    console.log(lancamentosSelecionados[0].data)
    if (datasUnicas.length > 1) {
      return null;
    }
  
    console.log(lancamentosSelecionados[0].data)
    return new Date(lancamentosSelecionados[0].data);
  }

  private updateList(list: LancamentoPrincipal[], origemLancamento: OrigemLancamento): void {
    const orderedNegativeList = [];
    const orderedPositiveList = [];

    for (const item of list) {
      const rawDate = new Date(item.data as unknown as string);
      item.data = new Date(rawDate.getFullYear(), rawDate.getMonth(), rawDate.getDate());
      if (item.valor >= 0) {
        orderedPositiveList.push(item);
      } else {
        orderedNegativeList.push(item);
      }
    }

    if (origemLancamento == OrigemLancamento.Banco) {
      this.lancamentosNegativosBanco = this.utilsService.sortBy(orderedNegativeList, 'valor');
      this.lancamentosPositivosBanco = this.utilsService.sortByDescending(orderedPositiveList, 'valor');
    }
    else {
      this.lancamentosNegativosCaixa = this.utilsService.sortBy(orderedNegativeList, 'valor');
      this.lancamentosPositivosCaixa = this.utilsService.sortByDescending(orderedPositiveList, 'valor');
    }
  }

  private updateValorFazerDesfazerConciliacao(lancamentoPrincipal: LancamentoPrincipal, isConciliando: boolean, firstLoad: boolean, lancamentoAgrupado?: LancamentoAgrupado): void {
    if (firstLoad) return;

    if (isConciliando) {
      if (lancamentoAgrupado)
        lancamentoPrincipal.valor -= lancamentoAgrupado.valor;
      else
        lancamentoPrincipal.valor = 0;
    }
    else {
      if (lancamentoAgrupado)
        lancamentoPrincipal.valor += lancamentoAgrupado.valor;
      else
        lancamentoPrincipal.valor = lancamentoPrincipal.valorTotal;
    }
    lancamentoPrincipal.valorX100 = Number(((lancamentoPrincipal.valor * 100)).toFixed(0));
  }

  // Atualiza status de conciliação dos lançamentos pais e filhos
  // Cria uma lista com os que estavam sem correspondencia
  // Atualiza o valor da soma dos pais
  private updateLancamentosBancoOnFazerOuDesfazerConciliacao(lancamentosBanco: Lancamento[], isConciliando: boolean, firstLoad: boolean): Lancamento[] {
    const lancamentosBancoDaConciliacao: Lancamento[] = [];

    lancamentosBanco?.forEach(lancamento => {
      // Se o lançamento é agrupador adiciona os filhos a lista e zera valor dele
      if (isLancamentoPrincipal(lancamento) && lancamento.ehAgrupador) {
        lancamento.lancamentosAgrupados?.forEach(child => {
          if (!child.conciliado) {
            lancamentosBancoDaConciliacao.push(child);
            child.conciliado = isConciliando;
          }
        });
        this.updateValorFazerDesfazerConciliacao(lancamento, isConciliando, firstLoad)
      }
      // Caso seja um lançamento filho ou um principal não agrupador adiciona na lista
      else {
        lancamentosBancoDaConciliacao.push(lancamento);
        // Se for um lançamento filho, atualizar soma do valor do pai
        if (isLancamentoAgrupado(lancamento)) {
          const parent = this.lancamentosBancoMap.get(lancamento.lancamentoPrincipalId);
          if (parent) {
            this.updateValorFazerDesfazerConciliacao(parent, isConciliando, firstLoad, lancamento)
            if (!isConciliando)
              parent.conciliado = false;
          }
        }
      }
      lancamento.conciliado = isConciliando;
    });

    return lancamentosBancoDaConciliacao;
  }

  public cleanSearchOriginSelection(): void {
    if (this.buscarEm === OrigemLancamento.Caixa) {
      this.lancamentosSelecionadosBanco = [];
      this.onUpdateSelectionBanco.emit();
    }
    else {
      this.lancamentosSelecionadosCaixa = [];
      this.onUpdateSelectionCaixa.emit();
    }
  }

  public aceitarCorrespondenciaSugeridaClick(conciliacaoSelecionada: Lancamento[]): void {
    let lancamentosBanco: Lancamento[];
    let lancamentosCaixa: Lancamento[];
    if (this.buscarEm === OrigemLancamento.Banco) {
      lancamentosBanco = conciliacaoSelecionada;
      lancamentosCaixa = this.lancamentosSelecionadosCaixa;
    }
    else {
      lancamentosBanco = this.lancamentosSelecionadosBanco;
      lancamentosCaixa = conciliacaoSelecionada;
    }

    this.aceitarCorrespondenciaClick(lancamentosBanco, lancamentosCaixa);
  }

  public aceitarCorrespondenciaSelecionada(): void {
    this.aceitarCorrespondenciaClick(this.lancamentosSelecionadosBanco, this.lancamentosSelecionadosCaixa);
  }

  public conciliarCheckBoxClick(cardEhBanco: boolean, lancamento: Lancamento): void {
    if (cardEhBanco)
      this.aceitarCorrespondenciaClick([lancamento], []);
    else
      this.aceitarCorrespondenciaClick([], [lancamento]);
  }

  private aceitarCorrespondenciaClick(lancamentosBanco: Lancamento[], lancamentosCaixa: Lancamento[]): void {
    if (!this.validarConciliacao(lancamentosBanco, 'banco')) return;
    if (!this.validarConciliacao(lancamentosCaixa, 'caixa')) return;

    const somaBanco = lancamentosBanco.reduce((soma, lancamento) => soma + lancamento.valorX100, 0) / 100;
    const somaCaixa = lancamentosCaixa.reduce((soma, lancamento) => soma + lancamento.valorX100, 0) / 100;

    if (somaBanco + somaCaixa !== 0) {
      const somaBancoStr = this.currencyPipe.transform(somaBanco, 'BRL', 'symbol', '1.2-2') || '';
      const somaCaixaStr = this.currencyPipe.transform(somaCaixa, 'BRL', 'symbol', '1.2-2') || '';
      const diferencaStr = this.currencyPipe.transform(somaBanco + somaCaixa, 'BRL', 'symbol', '1.2-2') || '';

      const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
        width: '467px',
        panelClass: 'no-padding-dialog',
        autoFocus: false
      });
      dialogRef.componentInstance.titulo = "Valores incorretos";
      dialogRef.componentInstance.mensagem = `Soma do banco: ${somaBancoStr}, soma no caixa: ${somaCaixaStr}, diferença: ${diferencaStr}`;
      dialogRef.componentInstance.mensagemBold = 'Tem certeza que deseja prosseguir?';
      dialogRef.componentInstance.acaoConfirmacao = this.fazerConciliacao.bind(this, lancamentosBanco, lancamentosCaixa);
    }
    else
      this.fazerConciliacao(lancamentosBanco, lancamentosCaixa);
  }

  // firstLoad é independente do id: correspondências "antigas" trazidas pelo reconciliar lançamentos
  // (ReconciliarLancamentosAntigosAsync) chegam com id=0 mesmo já estando refletidas no valor do
  // agrupador calculado pelo backend — usar `id !== 0` aqui as tratava como conciliação nova e
  // recalculava o valor em cima de um valor que já as contabilizava (duplicando o efeito).
  private fazerConciliacao(lancamentosBanco: Lancamento[], lancamentosCaixa: Lancamento[], id: number = 0, firstLoad: boolean = false): void {
    const lancamentosBancoDaConciliacao = this.updateLancamentosBancoOnFazerOuDesfazerConciliacao(lancamentosBanco, true, firstLoad);
    lancamentosCaixa?.forEach(l => l.conciliado = true);

    const correspondencia: CorrespondenciaConciliacao = {
      id: id,
      lancamentosBanco: lancamentosBancoDaConciliacao,
      lancamentosCaixa: lancamentosCaixa ?? []
    }
    this.correspondenciasConciliacao.push(correspondencia);
    this.updateCorrespondenciaConciliacaoIndex.emit(this.correspondenciasConciliacao.length - 1);
  }

  public removerCorrespondenciaClick(correspondenciaIndex: number): void {
    const correspondencia = this.correspondenciasConciliacao[correspondenciaIndex];
    this.correspondenciasConciliacao.splice(correspondenciaIndex, 1);

    correspondencia.lancamentosCaixa.forEach(l => l.conciliado = false);
    this.updateLancamentosBancoOnFazerOuDesfazerConciliacao(correspondencia.lancamentosBanco, false, false);
    this.updateCorrespondenciaConciliacaoIndex.emit(null);
  }

  public getInformacoesSalvarConciliacao(): InformacoesSalvarConciliacao {
    const correspondenciaDaConciliacao: CorrespondenciasIdsConciliacao[] = this.correspondenciasConciliacao.map(item => {
      const lancamentosIdBanco = item.lancamentosBanco.map(l => l.id);
      const lancamentosIdCaixa = item.lancamentosCaixa.map(l => l.id);

      return { id: item.id, lancamentosIdBanco: lancamentosIdBanco, lancamentosIdCaixa: lancamentosIdCaixa };
    });

    const info: InformacoesSalvarConciliacao = {
      contaId: this.dadosConciliacao.contaId,
      conciliacaoId: this.dadosConciliacao.conciliacaoId,
      correspondenciasDaConciliacao: correspondenciaDaConciliacao,
      correspondenciasIdsAntigos: this.dadosConciliacao.correspondenciasIdsAntigos
    };

    return info;
  }

  // Chamado após salvar um lote de lançamentos criado a partir de "Adicionar lote correspondente"
  // (ver lancamentos-table.component.ts). Busca os dados de conciliação de novo só para pegar os
  // lançamentos de caixa atualizados e ANEXA (não substitui) os que ainda não estão na tela —
  // assim as correspondências já montadas na sessão (que referenciam os objetos originais) e a
  // seleção atual não são perdidas, ao contrário do que aconteceria chamando setDadosConciliacao.
  public recarregarLancamentosCaixaAposAdicionarLoteCorrespondente(): void {
    const obterDados = this.dadosConciliacao.conciliacaoId === 0
      ? this.conciliacaoApiService.getDadosConciliacaoNova(this.dadosConciliacao.contaId)
      : this.conciliacaoApiService.getDadosConciliacaoExistente(this.dadosConciliacao.conciliacaoId);

    obterDados.subscribe((dados) => {
      const novosLancamentosCaixa = dados.lancamentosCaixa.filter(l => !this.lancamentosCaixaMap.has(l.id));
      if (novosLancamentosCaixa.length === 0) return;

      this.dadosConciliacao.lancamentosCaixa.push(...novosLancamentosCaixa);
      this.updateList(this.dadosConciliacao.lancamentosCaixa, OrigemLancamento.Caixa);
      novosLancamentosCaixa.forEach(l => this.lancamentosCaixaMap.set(l.id, l));
      this.onUpdateDadosConciliacao.emit();
    });
  }

  public removerLancamento(id: number, ehBanco: boolean): void {
    if (ehBanco) {
      this.dadosConciliacao.lancamentosBanco =
        this.lancamentosBanco.filter(x => x.id !== id);
    } else {
      this.dadosConciliacao.lancamentosCaixa =
        this.lancamentosCaixa.filter(x => x.id !== id);
    }
  
    this.onUpdateDadosConciliacao.emit();
  }

  private validarConciliacao(lancamentos: Lancamento[], origem: string): boolean {
    if (this.podeConciliar(lancamentos)) {
      return true;
    }
  
    this.messageService.showToastWarning(
      `Lançamentos do ${origem} já conciliados selecionados. Não é possível conciliar`
    );
  
    return false;
  }

  private podeConciliar(lancamentos: Lancamento[]): boolean {
    for (const lancamento of lancamentos) {
      // Se for um lançamento principal, verifica os agrupados
      if (isLancamentoPrincipal(lancamento) && lancamento.ehAgrupador) {
        const todosConciliados = lancamento.lancamentosAgrupados?.every(x => x.conciliado);
      
        if (todosConciliados) {
          return false;
        }
      }
      // Se for um lançamento normal
      else {      
        if (lancamento.conciliado) {
          return false;
        }
      }
    }
  
    // Se nenhum estiver conciliado
    return true;
  }
}
